import React, { useCallback, useEffect, useState } from 'react';
import { Accordion, FlexyButton, useModal } from '@europrocurement/flexy-components';
import {
    type CollectionDeDossierJsonldCollectiondossierRead,
    type ReglementsEtAcomptesCBJsonldAcompteRead,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { Box, Card, PaletteColor, Typography, useTheme } from '@mui/material';
import { addIcon } from '@europrocurement/l2d-icons';
import { FactureFormaliteApiObject } from '@europrocurement/l2d-domain';
import { useSnackbar } from 'notistack';
import {
    DocumentGed,
    useDossierDocumentFilesApi,
} from '@europrocurement/l2d-modules/modules/Dossiers/hooks';
import {
    DocumentPreviewProvider,
    useCurrentDossierCtx,
} from '@europrocurement/l2d-modules/modules/Dossiers/providers';
import CollectionProvision from '../../../CollectionProvision/CollectionProvision';
import ProvisionAdd from '../../../ProvisionAdd/ProvisionAdd';
import DocumentsModalContent from '../DocumentsModalContent/DocumentsModalContent';

type EncaissementsSectionProps = {
    collections: CollectionDeDossierJsonldCollectiondossierRead[];
    totalAcomptes: number;
    totalReglements: number;
    afterSubmitAcompte: () => void;
    reglements: ReglementsEtAcomptesCBJsonldAcompteRead[];
    acomptes: ReglementsEtAcomptesCBJsonldAcompteRead[];
    handleValidateInvoice: (invoice: FactureFormaliteApiObject) => Promise<void>;
};

const EncaissementsSection: React.FunctionComponent<EncaissementsSectionProps> = function ({
    collections,
    totalAcomptes,
    totalReglements,
    afterSubmitAcompte,
    reglements,
    acomptes,
    handleValidateInvoice,
}: EncaissementsSectionProps) {
    const { modalActions } = useModal();
    const { palette } = useTheme();
    const { getReglementsDocGed } = useDossierDocumentFilesApi();
    const { enqueueSnackbar } = useSnackbar();
    const { dossier } = useCurrentDossierCtx();

    const [reglementsDocs, setReglementsDocs] = useState<DocumentGed[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        const getAsyncReglementsDocs = async () => {
            if (!isLoading) {
                setIsLoading(true);
                if (collections) {
                    const dossIds: number[] = collections.flatMap(
                        (coll) => coll.dossiers?.map((doss) => doss.id as number) ?? [],
                    );
                    try {
                        const reglementDocs = await getReglementsDocGed(dossIds);

                        if (reglementDocs) {
                            setReglementsDocs(reglementDocs);
                        }
                    } catch (err) {
                        console.log(err);
                        enqueueSnackbar(
                            'Une erreur est survenue lors de la récupération des fichiers',
                            {
                                variant: 'error',
                            },
                        );
                    } finally {
                        setIsLoading(false);
                    }
                }
            }
        };

        getAsyncReglementsDocs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAddAttachmentFile = useCallback(
        (reglementId?: number) => {
            if (dossier && reglementId) {
                modalActions.call(
                    <DocumentPreviewProvider>
                        <DocumentsModalContent
                            dossier={dossier}
                            reglementsDocGed={reglementsDocs}
                            reglementId={reglementId}
                            setReglementsDocs={setReglementsDocs}
                        />
                    </DocumentPreviewProvider>,
                    true,
                    true,
                );
            }
        },
        [modalActions, reglementsDocs, dossier],
    );

    return (
        <Card
            id="encaissements"
            sx={{ boxShadow: 'none', padding: 0 }}
        >
            <Accordion
                sx={{
                    boxShadow: 'none',
                }}
                title="Encaissements"
                titlesSx={{
                    fontWeight: '600',
                    boxShadow: 'none',
                }}
                headerSx={{ boxShadow: 'none' }}
                expanded
                expandColor={palette.text.primary as unknown as PaletteColor}
                expandLabel={
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                            }}
                        >
                            <Typography
                                variant="l2dh7"
                                sx={{ marginRight: '10px', fontSize: '0.875rem' }}
                            >
                                Total encaissé :
                            </Typography>
                            <Typography
                                variant="l2dh7"
                                sx={{ color: 'text.primary' }}
                            >
                                {` ${totalAcomptes + totalReglements} €`}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                            }}
                        >
                            <Typography
                                variant="l2dh7"
                                sx={{ marginRight: '10px', fontSize: '0.875rem' }}
                            >
                                Crédit restant :
                            </Typography>
                            <Typography
                                variant="l2dh7"
                                sx={{ color: 'text.secondary' }}
                            >
                                {` ${totalAcomptes}
                                    €`}
                            </Typography>
                        </Box>
                    </Box>
                }
                footerActions={
                    collections.length === 1
                        ? [
                              <Box
                                  sx={{
                                      display: 'flex',
                                      flexDirection: 'row',
                                      justifyContent: 'end',
                                      alignItems: 'center',
                                      gap: '10px',
                                  }}
                              >
                                  <FlexyButton
                                      variant="outlined"
                                      color={palette.primary.dark}
                                      onClick={() => {
                                          modalActions.call(
                                              <ProvisionAdd
                                                  collectionId={collections[0].id as number}
                                                  afterSubmit={afterSubmitAcompte}
                                              />,
                                              true,
                                              false,
                                          );
                                      }}
                                      icon={addIcon}
                                      label="Ajouter un encaissement"
                                  />
                              </Box>,
                          ]
                        : []
                }
            >
                <CollectionProvision
                    acomptes={[...acomptes]}
                    reglements={[...reglements]}
                    afterSubmit={afterSubmitAcompte}
                    handleValidateInvoice={handleValidateInvoice}
                    reglementsDocs={reglementsDocs}
                    handleAddAttachmentFile={handleAddAttachmentFile}
                />
            </Accordion>
        </Card>
    );
};

export default EncaissementsSection;
